import type { ReactNode } from 'react';
import { Info } from 'lucide-react';
import { Card, CardContent, Tooltip, TooltipContent, TooltipTrigger, cn } from '@design-system';
import { approxMoney, money } from '../lib/format';

interface FigureProps {
  label: string;
  value: ReactNode;
  tone?: 'default' | 'positive' | 'attention';
  hint?: string;
}

export function Figure({ label, value, tone = 'default', hint }: FigureProps) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground flex items-center gap-1 text-xs font-medium uppercase tracking-wide">
        {label}
        {hint && (
          <Tooltip>
            <TooltipTrigger aria-label={`About ${label.toLowerCase()}`}>
              <Info className="size-3.5" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs normal-case tracking-normal">{hint}</TooltipContent>
          </Tooltip>
        )}
      </span>
      <strong
        className={cn(
          'text-2xl font-semibold tabular-nums',
          tone === 'positive' && 'text-primary',
          tone === 'attention' && 'text-yellow-800',
          tone === 'default' && 'text-gray-950',
        )}
      >
        {value}
      </strong>
    </div>
  );
}

interface CoverageSplitProps {
  projectedPayout: number;
  outOfPocket: number;
  short?: boolean;
  children?: ReactNode;
}

/** What insurance is projected to pay for the care scenario, and what's left for the consumer. */
export function CoverageSplit({ projectedPayout, outOfPocket, short, children }: CoverageSplitProps) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <Figure
            label={short ? 'Insurance could pay' : 'Projected LTC payments for this scenario'}
            value={approxMoney(projectedPayout)}
            tone="positive"
          />
          <Figure label="Your expected out-of-pocket care cost" value={approxMoney(outOfPocket)} />
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

interface MaxBenefitsAtAgeProps {
  age: number;
  monthlyMax: { home: number; facility: number } | null;
  totalMax: number;
}

/** The policy's limits in the year care starts, beyond what this scenario is projected to use. */
export function MaxBenefitsAtAge({ age, monthlyMax, totalMax }: MaxBenefitsAtAgeProps) {
  return (
    <section aria-label={`Policy maximums at age ${age}`} className="flex flex-col gap-3 border-t pt-6">
      <h3 className="text-sm font-semibold text-gray-950">At age {age}, this policy could pay up to</h3>
      <div className="grid gap-4 sm:grid-cols-3">
        {monthlyMax && (
          <>
            <Figure label="Monthly max at home" value={money(monthlyMax.home)} />
            <Figure label="Monthly max in a facility" value={money(monthlyMax.facility)} />
          </>
        )}
        <Figure label="Projected total max benefit" value={money(totalMax)} />
      </div>
    </section>
  );
}

export interface DetailRow {
  label: ReactNode;
  value: ReactNode;
}

export function DetailList({ rows }: { rows: DetailRow[] }) {
  return (
    <dl className="divide-y rounded-lg border bg-white">
      {rows.map((row, index) => (
        <div key={index} className="flex items-start justify-between gap-4 px-4 py-3 text-sm">
          <dt className="text-muted-foreground">{row.label}</dt>
          <dd className="text-right font-medium tabular-nums text-gray-950">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A bar showing how much of a care bill insurance pays. */
export function PaymentSplitBar({ total, insurance, label }: { total: number; insurance: number; label: string }) {
  const share = (part: number) => (total > 0 ? `${Math.min(100, (part / total) * 100)}%` : '0%');

  return (
    <div role="img" aria-label={label} className="flex h-3 w-full overflow-hidden rounded-full bg-gray-200">
      <span className="bg-primary h-full transition-[width] duration-300 ease-out" style={{ width: share(insurance) }} />
    </div>
  );
}
